import { Injectable } from '@angular/core';
import { BehaviorSubject, delay } from 'rxjs';
import { Models } from '@ibexfusion/models';
import { environment } from '../../../environments/environment';
import { ConnectionService } from '../connection/connection.service';
import { StorageService } from '../storage/storage.service';
import { EncryptionService } from '../encryption/encryption.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public $activeSession = new BehaviorSubject<any>(null);
  public $newValidatedAccount = new BehaviorSubject<boolean>(null);

  private device: Models.Device;
  private packageId = window['forcePackage'] || '66d02c84326ee0b8bf7e9b9e';


  constructor(private storage: StorageService, private connection: ConnectionService) {
    this.$activeSession.subscribe(session => {
      this.connection.setAuthenticationHeader(session);
    });

  }

  /* init session */

  public initSession() {
    this.getOrCreateDevice().then(device => {
      const subscription = this.storage.$ready.pipe(delay(0)).subscribe(ready => {
        if (ready) {
          subscription.unsubscribe();
          this.device = device;

          /* we write for each package a new device ( with account and frontProfile) */
          this.storage.set('device_' + this.packageId, JSON.stringify(this.device));

          let token = this.storage.get('rememberToken_' + this.packageId);
          const sessionSecretPart = EncryptionService.createRandomBytesBase64(16);
          const message = { secretPart: sessionSecretPart } as any;

          if (token) {
            message.credential = {
              type: 'ibexDeviceIdToken',
              loginName: this.device.id,
              accessToken: EncryptionService.hash(this.device.id + token)
            };
          }

          const box = EncryptionService.cryptBox(
            JSON.stringify(message),
            environment.serverPublicKey,
            EncryptionService.decodeBase64(this.device.privateKey));

          this.createSession(box, sessionSecretPart);
        }
      })
    });
  }

  private createSession(box: string, sessionSecretPart: string, newValidation = false) {
    this.connection.create('sessions', { deviceId: this.device.id, box, type: 'ibex' }).then(result => {
      if (!result) {
        this.sessionFallback();
      }
      let session = result.data;

      session = EncryptionService.openBox(session, environment.serverPublicKey, this.device.privateKey);
      session = JSON.parse(session);
      session.secret = EncryptionService.encodeBase64(EncryptionService.mergeTypedArrays(
        EncryptionService.decodeBase64(session.secret),
        EncryptionService.decodeBase64(sessionSecretPart)
      ));
      this.$activeSession.next(session);
      if (newValidation) {
        this.$newValidatedAccount.next(true);
      }
    }).catch(err => {
      console.log('error on create Session - init a new session with new device, profile ...', err);
      this.initNewSession();
    });
  }

  private initNewSession() {
    const subscription = this.storage.$ready.pipe(delay(0)).subscribe(() => {
      this.storage.remove('device_' + this.packageId);
      this.initSession();
      subscription.unsubscribe();
    })
  }

  private sessionFallback() {
    // todo - check if we really need a fallback ?
    const subscription = this.storage.$ready.pipe(delay(0)).subscribe(ready => {
      if (ready) {
        subscription.unsubscribe();
        let profile = this.storage.get('profile_' + this.packageId);
        if (profile) {
          this.$activeSession.next({ profile });
        } else {
          throw new Error('no session created');
        }
      }
    })
  }

  /* profile auth */

  private getOrCreateDevice(): Promise<any> {
    return new Promise<any>(resolve => {
      const subscription = this.storage.$ready.pipe(delay(0)).subscribe(ready => {
        if (ready) {
          subscription.unsubscribe();
          let device = JSON.parse(this.storage.get('device_' + this.packageId));
          if (!device) {
            this.createDevice().then(newDevice => {
              resolve(newDevice);
            });
          } else {
            resolve(device);
          }
        }
      });
    });
  }

  private createDevice(): Promise<any> {
    // todo validate this language with availableLanguages from singleton translationService
    // const userLanguage = this.translationService.validateLanguage(navigator.language.split('-')[0]);
    const userLanguage = navigator.language.split('-')[0];
    const keyPair = EncryptionService.createKeyPairBase64();
    const initDeviceData = {
      publicKey: keyPair.publicKey,
      // model: this.userDevice.model || 'web',
      // manufacturer: this.userDevice.manufacturer || 'unknown',
      // uuid: this.userDevice.uuid,
      language: userLanguage,
      // platform: this.userDevice.platform || 'unknown',
      // version: this.userDevice.version || 'unknown',
      // serial: this.userDevice.serial || 'unknown',
      // cordova: this.userDevice.cordova || 'unknown',
      type: 'web',
      mode: 'ibex',
      ibexPackage: this.packageId
    };
    return this.connection.create('devices', initDeviceData).then(result => {

      if (!result) {
        throw new Error('no device created');
      }

      const device = result.data;
      device.publicKey = keyPair.publicKey;
      device.privateKey = keyPair.privateKey;

      return device;
    });
  }
}

